import { Button, Input } from '@orbind/ui'
import { useEffect, useRef, useState } from 'react'

/**
 * Seletor padrao das abas de configuracao do quadro.
 *
 * Mesmo comportamento em toda aba (areas, tipos de item, produtos, demandas,
 * empresas e membros): busca + "Adicionar" lado a lado; clicar na busca abre
 * a lista/arvore; digitar filtra mantendo os ancestrais; UM clique seleciona
 * (habilita Adicionar) e DOIS cliques adicionam ou removem sem fechar; a
 * lista fecha pelo "x", clicando fora ou ao usar o Adicionar.
 */

export type ScopeNode = {
  id: string
  label: string
  /** Texto discreto a direita (nivel do portfolio, tipo, contagem…). */
  meta?: string
  icon?: string
  parentId: string | null
  depth: number
  /** Agrupadores (grupo de demanda, area) nao entram na selecao. */
  selectable: boolean
}

export type ScopePickerProps = {
  id?: string
  placeholder: string
  nodes: readonly ScopeNode[]
  value: readonly string[]
  onChange: (ids: string[]) => void
  /** Mensagem quando nada foi escolhido (o quadro nao restringe). */
  emptyMessage: string
}

export function ScopePicker({ id, placeholder, nodes, value, onChange, emptyMessage }: ScopePickerProps) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const parentOf = (node: ScopeNode) => nodes.find((n) => n.id === node.parentId)
  const query = search.trim().toLowerCase()

  // com busca: os nos que casam e todos os ancestrais deles
  const matched = new Set<string>()
  if (query !== '') {
    for (const node of nodes) {
      if (!node.label.toLowerCase().includes(query)) continue
      matched.add(node.id)
      let parent = parentOf(node)
      while (parent) {
        matched.add(parent.id)
        parent = parentOf(parent)
      }
    }
  }

  const visible = nodes.filter((node) => {
    if (query !== '') return matched.has(node.id)
    let parent = parentOf(node)
    while (parent) {
      if (!expanded.has(parent.id)) return false
      parent = parentOf(parent)
    }
    return true
  })

  const toggleExpand = (nodeId: string) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })

  const toggleValue = (nodeId: string) =>
    onChange(value.includes(nodeId) ? value.filter((x) => x !== nodeId) : [...value, nodeId])

  const chosen = value.map((chosenId) => nodes.find((n) => n.id === chosenId) ?? null)

  return (
    <div className="kb__demandpicker" ref={boxRef}>
      <div className="kb__searchrow">
        <Input
          id={id}
          value={search}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
        />
        <Button
          icon="fas fa-plus"
          disabled={picked === null || value.includes(picked)}
          onClick={() => {
            if (!picked) return
            onChange([...value, picked])
            setPicked(null)
            setOpen(false)
          }}
        >
          Adicionar
        </Button>
      </div>

      {open ? (
        <div className="kb__demandtree desc-scroll">
          <button type="button" className="kb__treeclose" aria-label="Fechar lista" title="Fechar lista" onClick={() => setOpen(false)}>
            <i className="fas fa-times" aria-hidden />
          </button>
          {visible.map((node) => {
            const hasChildren = nodes.some((n) => n.parentId === node.id)
            const isOpen = expanded.has(node.id)
            const already = value.includes(node.id)
            return (
              <button
                key={node.id}
                type="button"
                className={`kb__treerow${picked === node.id ? ' kb__treerow--on' : ''}${
                  node.selectable ? '' : ' kb__treerow--group'
                }`}
                style={{ paddingLeft: 8 + node.depth * 18 }}
                title={
                  !node.selectable
                    ? node.label
                    : already
                      ? 'Já está no quadro — dois cliques removem'
                      : `${node.label} (um clique seleciona, dois adicionam)`
                }
                onClick={() => {
                  if (node.selectable) setPicked(node.id)
                  else toggleExpand(node.id)
                }}
                onDoubleClick={() => {
                  if (!node.selectable) return
                  setOpen(true)
                  toggleValue(node.id)
                }}
              >
                <i
                  className={`fas fa-caret-${isOpen ? 'down' : 'right'}`}
                  aria-hidden
                  style={{ visibility: hasChildren && query === '' ? 'visible' : 'hidden' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpand(node.id)
                  }}
                />
                <i className={node.icon ?? 'fas fa-cube'} aria-hidden />
                <span className="kb__picktitle">{node.label}</span>
                {node.meta ? <span className="kb__treecount">{node.meta}</span> : null}
                {already ? <i className="fas fa-check" aria-hidden /> : null}
              </button>
            )
          })}
          {visible.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12.5, color: 'var(--ob-text-muted)' }}>Nenhum item encontrado.</div>
          ) : null}
        </div>
      ) : null}

      {value.length === 0 ? (
        <div className="kb__pickempty">{emptyMessage}</div>
      ) : (
        <ul className="kb__picklist">
          {value.map((chosenId, index) => {
            const node = chosen[index]
            return (
              <li
                key={chosenId}
                className="kb__pickrow"
                title="Dois cliques removem do quadro"
                onDoubleClick={() => onChange(value.filter((x) => x !== chosenId))}
              >
                {node?.meta ? <span className="kb__pickcode">{node.meta}</span> : null}
                <i className={node?.icon ?? 'fas fa-cube'} aria-hidden style={{ color: 'var(--ob-text-subtle)', fontSize: 11 }} />
                <span className="kb__picktitle">{node?.label ?? 'Item não encontrado'}</span>
                <button
                  type="button"
                  className="kb__pickremove"
                  aria-label={`Remover ${node?.label ?? 'item'}`}
                  title="Remover da lista"
                  onClick={() => onChange(value.filter((x) => x !== chosenId))}
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
